import type { CoverageFacility, CoverageNurse } from "@vellum/vellum-api";

const EARTH_RADIUS_MILES = 3958.8;

type LngLat = { lat: number; long: number };

/** Great-circle distance in miles. */
export function milesBetween(a: LngLat, b: LngLat): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLong = toRad(b.long - a.long);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLong / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(h));
}

/** Straight-line miles from each facility to its closest nurse (Infinity when there are none). */
export function nearestNurseMiles(
  facilities: CoverageFacility[],
  nurses: CoverageNurse[],
): Map<string, number> {
  const nearest = new Map<string, number>();
  for (const facility of facilities) {
    let best = Infinity;
    for (const nurse of nurses) {
      best = Math.min(best, milesBetween(facility, nurse));
    }
    nearest.set(facility.id, best);
  }
  return nearest;
}

/** A closed ring approximating a circle of `radiusMiles` around a point, as [lng, lat] pairs. */
export function circleRing(
  center: LngLat,
  radiusMiles: number,
  steps = 48,
): [number, number][] {
  const angular = radiusMiles / EARTH_RADIUS_MILES;
  const lat = (center.lat * Math.PI) / 180;
  const long = (center.long * Math.PI) / 180;
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (2 * Math.PI * i) / steps;
    const pLat = Math.asin(
      Math.sin(lat) * Math.cos(angular) +
        Math.cos(lat) * Math.sin(angular) * Math.cos(bearing),
    );
    const pLong =
      long +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
        Math.cos(angular) - Math.sin(lat) * Math.sin(pLat),
      );
    ring.push([(pLong * 180) / Math.PI, (pLat * 180) / Math.PI]);
  }
  return ring;
}

/** [[west, south], [east, north]] around the given points, or null when there are none. */
export function boundsOf(
  points: LngLat[],
): [[number, number], [number, number]] | null {
  if (points.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const { lat, long } of points) {
    west = Math.min(west, long);
    east = Math.max(east, long);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  return [
    [west, south],
    [east, north],
  ];
}

/** Groups whose facilities come within this many miles of each other count as neighbors. */
const NEIGHBOR_MILES = 40;

/**
 * Give each group (region, company, ...) an index into a palette of
 * `paletteSize` colors so that neighboring groups never share one. Groups are
 * neighbors when their facility bounding boxes, padded by NEIGHBOR_MILES,
 * overlap. Greedy coloring, biggest groups first; if every color is taken
 * nearby, reuse the least-used.
 */
export function assignGroupColors(
  facilities: CoverageFacility[],
  groupOf: (facility: CoverageFacility) => string,
  paletteSize: number,
): Map<string, number> {
  const byGroup = new Map<string, CoverageFacility[]>();
  for (const facility of facilities) {
    const key = groupOf(facility);
    const list = byGroup.get(key) ?? [];
    list.push(facility);
    byGroup.set(key, list);
  }

  // ~69 miles per degree of latitude; longitude degrees shrink with latitude,
  // so pad by the widest (southernmost US) case to stay conservative.
  const padLat = NEIGHBOR_MILES / 69;
  const padLong = NEIGHBOR_MILES / 50;
  const boxes = [...byGroup.entries()]
    .map(([groupId, members]) => ({
      groupId,
      size: members.length,
      box: boundsOf(members)!,
    }))
    .sort((a, b) => b.size - a.size);

  const overlaps = (
    a: [[number, number], [number, number]],
    b: [[number, number], [number, number]],
  ) =>
    a[0][0] - padLong <= b[1][0] &&
    b[0][0] - padLong <= a[1][0] &&
    a[0][1] - padLat <= b[1][1] &&
    b[0][1] - padLat <= a[1][1];

  const colors = new Map<string, number>();
  const uses = new Array<number>(paletteSize).fill(0);
  for (const { groupId, box } of boxes) {
    const taken = new Set(
      boxes
        .filter((other) => colors.has(other.groupId) && overlaps(box, other.box))
        .map((other) => colors.get(other.groupId)!),
    );
    const free = uses
      .map((count, index) => ({ count, index }))
      .filter(({ index }) => !taken.has(index));
    const pick = (free.length ? free : uses.map((count, index) => ({ count, index })))
      .sort((a, b) => a.count - b.count)[0].index;
    colors.set(groupId, pick);
    uses[pick] += 1;
  }
  return colors;
}
